/**
 * Pure merge of a device's BRANDING state (device-config-service `GET
 * /devices/{deviceType}/status`) with its RELEASE state (Phase B rings/pins,
 * Phase C ledger) into ONE row per device.
 *
 * There is exactly one fleet table in this product — the Fleet tab of
 * `/pos-config`. This module exists so that table can gain release columns
 * without a second table being invented somewhere else, and so the rollout
 * view can count devices with the SAME definition of "on target" the table
 * renders (see `classifyDeviceRelease` / `countFleetReleaseStatuses`).
 *
 * PURITY CONTRACT: no network, no React, no AWS, no `Date.now()` unless the
 * caller declines to supply `now`. Everything here is testable without mocks.
 * Do not import a client into this file.
 */

import type { FleetRegisterLink } from '@platform/shared-types';

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

/**
 * A `device#<id>` row exactly as `GET /devices/{deviceType}/status` returns it.
 * Every field is optional except `deviceId` because the row is written
 * incrementally by four different writers (check-in, setDeviceChannel,
 * setDeviceRelease, and branding publish) and a device that has only ever sent
 * a minimal check-in carries almost none of them.
 */
export interface FleetDeviceRecord {
  deviceId: string;
  deviceType?: string;
  registerName?: string | null;
  locationName?: string | null;
  locationId?: string | null;
  /** Branding channel (canary/stable) — NOT the release ring. Two different axes. */
  assignedChannel?: string | null;
  channel?: string | null;
  bootVersion?: number | null;
  loadingVersion?: number | null;
  bootInstalled?: boolean | null;
  error?: string | null;
  /** Last check-in, epoch ms. Absent for a device that has never checked in. */
  updatedAt?: number | null;
  /** Live check-in app version. THE authority on what the device is running. */
  appVersion?: string | null;
  appId?: string | null;
  /**
   * What the HARDWARE calls itself — Android `Build.MODEL` / `Build.BRAND`,
   * e.g. `TC-TOUCH-K10` / `TCANG`.
   *
   * The only field on a check-in that nobody typed, and therefore the only one
   * that can contradict the others. Absent on every device that has not yet
   * taken a build carrying `DeviceControls.getHardwareIdentity`, which is why
   * nothing here may treat absence as a fault.
   */
  hardwareModel?: string | null;
  hardwareBrand?: string | null;
  /** Fleet Monitoring 2026-10-06 — see CheckinRequestSchema. Absent on older builds. */
  pendingUploads?: { sales: number; other: number; oldestAt?: number } | null;
  updateState?: FleetUpdateState | null;
  updateVersion?: string | null;
  updateError?: string | null;
  /** Phase B: canary | pilot | stable. Absent rows default to stable server-side. */
  releaseRing?: string | null;
  /** Phase B: a per-device pin. A pin beats its ring. */
  pinnedVersion?: string | null;
  pinnedAt?: number | null;
  pinnedBy?: string | null;
  /**
   * Task 10 — the remote off switch. Owned by `setDeviceAutoUpdate`, handed
   * back on check-in. ABSENT, NULL AND UNDEFINED ALL MEAN ON — only an
   * explicit stored `false` means off. Mirrors `deviceStatus.ts`'s own
   * `prev.autoUpdate === false ? false : true`; do not "simplify" that away.
   */
  autoUpdate?: boolean | null;
}

/**
 * A hardware unit row (hardware-service `DeviceUnitRow`), narrowed to what the
 * fleet view reads.
 *
 * `deviceId` is DELIBERATELY optional and today is never populated: Phase C's
 * OTA→unit sync is deferred because `unit.terminalId` turned out to be a
 * payment-processor TID, not a device identity, so no deviceId→unit mapping
 * exists yet. Until one does, callers pass `[]` and the install column is
 * simply absent. When the mapping lands, populating `deviceId` is the only
 * change needed here.
 */
export interface FleetUnitRecord {
  unitId: string;
  /**
   * The PAIRED TERMINAL's id (`term_…`), when it is known — the same value the
   * device sends as `deviceId` on check-in.
   *
   * NOT `unit.terminalId`, which is a payment-processor TID and identifies a
   * card reader, not a till. Joining on that field matches the wrong device.
   */
  deviceId?: string | null;
  /** `RTA-######`. Also the fallback join key — see {@link buildFleetRows}. */
  trackingId?: string;
  modelId?: string;
  serialNumber?: string;
  /** The order this unit shipped on, so a fleet row can be traced back to it. */
  orderId?: string;
  /**
   * The build the INSTALL PANEL recorded. Written by
   * `DeviceInstallPanel.tsx` via `hardwareApi.upsertUnit` at install time and
   * by nothing else — OTA does not update it. It is "last known from install",
   * never ground truth, and must never override a live check-in `appVersion`.
   */
  appBuild?: string | null;
  /** From the unit's `osInstall` stamp — server-owned, only a verified run writes it. */
  installedAt?: number | null;
  installSource?: 'local' | 'dev' | 'prod' | null;
  installTarget?: string | null;
  installVersionCode?: number | null;
  installedBy?: string | null;
  /**
   * The unit's own fulfilment state (`configured`, `deployed`, …).
   *
   * The fleet view is mostly devices already in the field, and a reinstall can
   * only be done to one still on the bench — the install runs over local adb.
   * Carrying the state lets the row say so BEFORE the button is pressed
   * instead of rendering an action that always fails.
   */
  state?: string | null;
  /** Set while a requested reinstall has not been carried out yet. */
  reinstallRequestedAt?: number | null;
}

/** What the server resolved this device should be running (mirrors device-config-service `resolveTarget`). */
export interface ResolvedTargetInfo {
  version?: string | null;
  /** 'pin' when a per-device pin decided it, 'ring' when the device's ring did. */
  source?: 'pin' | 'ring' | null;
  url?: string | null;
  hash?: string | null;
}

/**
 * `fleetTargetKey(deviceType, deviceId)` → the target resolved for that device,
 * or null/absent when nothing resolved.
 *
 * NOT keyed by `deviceId` alone: `deviceId` is client-supplied and stored as
 * the SORT key inside the `deviceType` partition (device-config-service
 * `deviceStatus.ts`), so it is unique only WITHIN a device type. Two types can
 * both hold `POS-01`, and a bare-deviceId map would let one device be judged
 * against the other type's resolved target — a value paired with the wrong
 * companion value.
 */
export type ResolvedTargetMap = Record<string, ResolvedTargetInfo | null | undefined>;

/**
 * registerId → the register behind it. `registerId` IS the fleet's `deviceId`
 * and is globally unique, so unlike ResolvedTargetMap this needs no compound
 * key: two device types cannot collide on it.
 */
export type FleetRegisterLinkMap = Record<string, FleetRegisterLink | undefined>;

/** Separator that cannot occur in a deviceType or deviceId, so the key cannot be forged by concatenation. */
const KEY_SEP = '\u0000';

/**
 * The one key shape for {@link ResolvedTargetMap}. Both the producer
 * ({@link resolvedTargetMapFromEntries}) and every consumer must use it.
 */
export function fleetTargetKey(deviceType: string | null | undefined, deviceId: string): string {
  return `${deviceType || ''}${KEY_SEP}${deviceId}`;
}

/**
 * One entry of `GET /devices/{deviceType}/resolved-targets` (Task D6):
 * `{ deviceId, target: ResolvedTarget | null }`.
 */
export interface ResolvedTargetEntry {
  deviceId?: string | null;
  /** Present when the server states it per entry; otherwise the caller supplies the response's `deviceType`. */
  deviceType?: string | null;
  target?: ResolvedTargetInfo | null;
}

/**
 * Turns the resolved-targets response into the map {@link buildFleetRows}
 * takes. Pure — the network call lives in `services/deviceConfigApi.ts`.
 *
 * An entry with `target: null` (the server resolved NOTHING for that device —
 * no ring pointer, or a pin whose build is gone) is deliberately left OUT of
 * the map rather than written as an empty target. Both read the same
 * downstream (`targetVersion` undefined → `unknown`), but leaving it out keeps
 * "the endpoint answered null" and "the endpoint was never asked"
 * indistinguishable at the row level, which is correct: neither is evidence
 * that a device is on target.
 *
 * `into` lets a caller accumulate several device types into one map. Entries
 * are keyed by {@link fleetTargetKey} — deviceType AND deviceId — because
 * `deviceId` is only unique WITHIN a device type: two types can legitimately
 * both hold `POS-01`, and keying by deviceId alone would silently overwrite one
 * with the other, judging a device against a build it can never run.
 *
 * `deviceType` comes from the entry when the server states it there, else from
 * the `deviceType` argument (the response's top-level field). Pass it.
 */
export function resolvedTargetMapFromEntries(
  entries: readonly ResolvedTargetEntry[] | null | undefined,
  into: ResolvedTargetMap = {},
  deviceType?: string | null,
): ResolvedTargetMap {
  for (const e of entries || []) {
    const id = str(e?.deviceId);
    if (!id) continue;
    const version = str(e?.target?.version);
    if (!version) continue;
    const source = e?.target?.source;
    into[fleetTargetKey(str(e?.deviceType) ?? deviceType, id)] = {
      version,
      source: source === 'pin' || source === 'ring' ? source : null,
      url: e?.target?.url ?? null,
      hash: e?.target?.hash ?? null,
    };
  }
  return into;
}

/** A Phase C ledger row as `GET /devices/{deviceType}/devices/{deviceId}/ledger` returns it. */
export interface FleetLedgerEntry {
  deviceId?: string;
  deviceType?: string;
  at: number;
  fromVersion?: string | null;
  toVersion?: string | null;
  appId?: string | null;
  via?: string | null;
  source?: string | null;
}

// ---------------------------------------------------------------------------
// Output
// ---------------------------------------------------------------------------

/**
 * How a device stands against ITS OWN resolved target. Exhaustive and mutually
 * exclusive: every row is exactly one of these, so counting is a plain tally
 * with no double-count and no gap.
 */
export type FleetUpdateState = 'idle' | 'checking' | 'downloading' | 'ready' | 'waiting_for_idle' | 'installing' | 'failed';

export type FleetReleaseStatus = 'on-target' | 'pinned' | 'stale' | 'behind' | 'unknown';

export const FLEET_RELEASE_STATUSES: readonly FleetReleaseStatus[] = [
  'on-target',
  'pinned',
  'stale',
  'behind',
  'unknown',
];

/** A device that has not checked in for this long is stale. Overridable per call. */
export const DEFAULT_STALE_AFTER_MS = 24 * 60 * 60 * 1000;

export interface FleetRow {
  /** Stable React key. The deviceId when there is one, else `unit:<unitId>`. */
  key: string;
  deviceId?: string;
  deviceType?: string;
  unitId?: string;
  serialNumber?: string;
  /** Unit-side identity and provenance, for the photo and the order trace. */
  trackingId?: string;
  modelId?: string;
  orderId?: string;
  /** From `osInstall` — when the OS actually went on, and what put it there. */
  installedAt?: number;
  /** The unit's fulfilment state, when a unit is joined to this row. */
  unitState?: string;
  /** Set while a requested reinstall is outstanding. */
  reinstallRequestedAt?: number;
  installSource?: 'local' | 'dev' | 'prod';
  installTarget?: string;
  installVersionCode?: number;
  installedBy?: string;

  // --- columns the Fleet tab already rendered before release data existed.
  // Passed through UNCHANGED (nulls preserved) — the tab's own `?? '—'`
  // fallbacks keep working exactly as they did.
  registerName?: string | null;
  locationName?: string | null;
  locationId?: string | null;
  assignedChannel?: string | null;
  bootVersion?: number | null;
  loadingVersion?: number | null;
  bootInstalled?: boolean | null;
  error?: string | null;
  updatedAt?: number | null;

  // --- release columns (all additive; all absent for a device with no release data)
  /** Live check-in version. Authoritative. */
  appVersion?: string;
  appId?: string;
  /** `unit.appBuild` — last known FROM INSTALL. Never authoritative. */
  lastKnownInstallBuild?: string;
  /** What to show in the version column: the live value when there is one, else the install hint. */
  displayVersion?: string;
  /** Which of the two `displayVersion` came from, so the UI can label it honestly. */
  versionSource: 'checkin' | 'install' | 'none';
  /** Phase B ring. Defaults to 'stable', matching the server. */
  releaseRing: string;
  pinnedVersion?: string;
  /**
   * Task 10 — the remote off switch, defaulted to ON (`true`) when the
   * device row carries no flag. Never `undefined`: every row has an answer,
   * so the card can always decide whether to show the "auto-update OFF" pill.
   */
  autoUpdate: boolean;
  targetVersion?: string;
  targetSource?: 'pin' | 'ring';
  /**
   * True only when BOTH the live version and the resolved target are known and
   * they differ. A pinned device sitting on its pin has zero drift; an unknown
   * target is never drift.
   */
  drift: boolean;
  releaseStatus: FleetReleaseStatus;

  /** What the hardware reported it is, when it said. See {@link FleetDeviceRecord}. */
  hardwareModel?: string;
  hardwareBrand?: string;
  /** What the till still holds unsent. Undefined = this build does not report it (NOT "none"). */
  pendingUploads?: { sales: number; other: number; oldestAt?: number };
  /** The till's own update state, as it last reported it. Undefined = not reported. */
  updateState?: FleetUpdateState;
  updateVersion?: string;
  updateError?: string;

  hasDeviceRecord: boolean;
  hasUnitRecord: boolean;
  /** The raw source row, for callers that need a field this type has not surfaced yet. */
  device?: FleetDeviceRecord;

  /** The tenant that owns this register, from the register link. */
  tenantId?: string;
  /** `RT-XXXX-XXXX` — what the till types in to claim itself. */
  provisionCode?: string;
  /**
   * HOW the unit on this row was decided.
   *
   * 'register' is the registers table stating it outright. 'name' is the old
   * tracking-id-in-register-name heuristic, kept because it is the only thing
   * that joins a register created before the link existed. 'none' means no
   * unit — which is a fact about our records, not about the device.
   */
  unitJoinSource: 'register' | 'name' | 'none';
}

// ---------------------------------------------------------------------------
// HARDWARE IDENTITY — does the metal agree with the paperwork?
// ---------------------------------------------------------------------------

/**
 * The possible answers to "is this device the model we think it is?".
 *
 * FIVE, not two, because the four non-mismatch cases are genuinely different
 * and an operator does something different about each. Collapsing them into
 * "ok / not ok" is what turns a data-entry gap into a hardware alarm, and a
 * hardware alarm nobody can act on is one people learn to ignore.
 *
 * - `match`        the reported model is one this catalog row claims. Silent.
 * - `mismatch`     it is NOT. Somebody scanned the wrong box, or a unit was
 *                  swapped on the bench and nobody updated the record. THE
 *                  ONLY ONE WORTH INTERRUPTING ANYONE FOR.
 * - `unclaimed`    the catalog model exists but lists no `hardwareIds`, so
 *                  there is nothing to compare against. A gap in OUR data.
 * - `not-reported` the device has not said what it is — every terminal running
 *                  a build older than this feature, and every web terminal.
 *                  Says nothing about the hardware at all.
 * - `no-model`     the row is not joined to a catalog model (no unit, or a
 *                  model since deleted), so the question cannot be asked.
 */
export type HardwareMatch = 'match' | 'mismatch' | 'unclaimed' | 'not-reported' | 'no-model';

/** The catalog model fields this comparison reads. */
export interface HardwareClaim {
  hardwareIds?: readonly string[];
}

/**
 * Compare what a device SAYS it is against what its catalog model CLAIMS.
 *
 * Comparison is case-insensitive and trims, because `ro.product.model` is a
 * vendor-set string an operator copies by hand into the catalog and
 * `TC-Touch-K10` and `TC-TOUCH-K10` are the same machine. It is otherwise
 * EXACT — no prefix or substring matching. A device that reports `K10-V2` is
 * either a model somebody has to add to the list or a genuine mix-up, and
 * fuzzy-matching it would answer "probably fine" to both.
 */
export function classifyHardwareMatch(
  reported: string | null | undefined,
  claim: HardwareClaim | null | undefined,
): HardwareMatch {
  const said = (reported || '').trim();
  if (!claim) return 'no-model';
  if (!said) return 'not-reported';
  const claimed = (claim.hardwareIds || []).map((h) => (h || '').trim()).filter(Boolean);
  if (claimed.length === 0) return 'unclaimed';
  return claimed.some((h) => h.toLowerCase() === said.toLowerCase()) ? 'match' : 'mismatch';
}

/** Only a mismatch is a finding. Everything else is silence or a to-do. */
export function isHardwareMismatch(m: HardwareMatch): boolean {
  return m === 'mismatch';
}

export interface FleetRowOptions {
  /** Epoch ms treated as "now". Supply it in tests; defaults to `Date.now()`. */
  now?: number;
  /** Staleness threshold. Defaults to {@link DEFAULT_STALE_AFTER_MS}. */
  staleAfterMs?: number;
  /**
   * The device type these rows belong to, used to look targets up by
   * {@link fleetTargetKey} when a device row does not carry `deviceType`
   * itself. Pass it whenever the caller knows it (the Fleet table does).
   */
  deviceType?: string;
}

// ---------------------------------------------------------------------------
// Classification — the ONE definition of "on target" in this product
// ---------------------------------------------------------------------------

/**
 * The shape `classifyDeviceRelease` needs. A `FleetRow` satisfies it, and so
 * does any hand-built object with these four fields — so a caller that only
 * has counts to produce does not have to build full rows.
 */
export interface ClassifiableDevice {
  /** Live check-in version. */
  appVersion?: string | null;
  /** The device's pin, if any. */
  pinnedVersion?: string | null;
  /** The version RESOLVED for this device (pin beats ring) — not the global newest build. */
  targetVersion?: string | null;
  /** Last check-in, epoch ms. Absent/null means never seen. */
  updatedAt?: number | null;
  /** Alias accepted for callers whose rows name it `lastSeenAt`. */
  lastSeenAt?: number | null;
}

/**
 * Which single bucket this device belongs in.
 *
 * PRECEDENCE (load-bearing — a device can satisfy several of these at once and
 * must be counted exactly once):
 *  1. `stale`     — never checked in, or last check-in older than the threshold.
 *                   Stale beats pinned: a pinned device that stopped reporting
 *                   is a device you cannot vouch for, and counting it as
 *                   "pinned" would hide it. This is the pinned/stale overlap a
 *                   naive implementation double-counts.
 *  2. `pinned`    — held on a specific version by an operator. Deliberately
 *                   NOT "on target" even though it is on its resolved target:
 *                   a rollout wants pins visible, not folded into success.
 *  3. `unknown`   — no resolved target, or no live version to compare. Never
 *                   claim a device is on target on missing evidence.
 *  4. `on-target` — live version equals the resolved target.
 *  5. `behind`    — live version differs from the resolved target.
 *
 * Drift is judged against the device's OWN resolved target, never the global
 * newest build: a device pinned to an older version is on its target.
 */
export function classifyDeviceRelease(device: ClassifiableDevice, options: FleetRowOptions = {}): FleetReleaseStatus {
  const now = options.now ?? Date.now();
  const staleAfterMs = options.staleAfterMs ?? DEFAULT_STALE_AFTER_MS;
  const lastSeen = device.updatedAt ?? device.lastSeenAt ?? null;

  if (lastSeen == null || now - lastSeen > staleAfterMs) return 'stale';
  if (device.pinnedVersion) return 'pinned';
  if (!device.appVersion || !device.targetVersion) return 'unknown';
  return device.appVersion === device.targetVersion ? 'on-target' : 'behind';
}

export type FleetReleaseStatusCounts = Record<FleetReleaseStatus, number> & { total: number };

/**
 * Tally of {@link classifyDeviceRelease} over a fleet. Buckets are mutually
 * exclusive and always sum to `total`, including for an empty fleet (all
 * zeroes, `total` 0 — the caller renders "no devices", never "0 of 0").
 */
export function countFleetReleaseStatuses(
  devices: readonly ClassifiableDevice[],
  options: FleetRowOptions = {},
): FleetReleaseStatusCounts {
  const counts = {
    'on-target': 0,
    pinned: 0,
    stale: 0,
    behind: 0,
    unknown: 0,
    total: 0,
  } as FleetReleaseStatusCounts;
  for (const d of devices) {
    counts[classifyDeviceRelease(d, options)] += 1;
    counts.total += 1;
  }
  return counts;
}

/**
 * Tally of the `releaseStatus` ALREADY STAMPED on each row by
 * {@link buildFleetRows} — no re-classification, so no second `Date.now()`.
 *
 * {@link countFleetReleaseStatuses} classifies from scratch and is correct for
 * a caller holding raw devices (the rollout view). A caller that already holds
 * rows must use THIS one: classifying twice means two clocks, and a device
 * sitting on the staleness boundary could be tallied `pinned` by a filter pill
 * while the row list filters it as `stale` — a pill reading "pinned 1" that
 * opens an empty table. One classification per render.
 */
export function tallyFleetRowStatuses(rows: readonly FleetRow[]): FleetReleaseStatusCounts {
  const counts = {
    'on-target': 0,
    pinned: 0,
    stale: 0,
    behind: 0,
    unknown: 0,
    total: 0,
  } as FleetReleaseStatusCounts;
  for (const r of rows) {
    counts[r.releaseStatus] += 1;
    counts.total += 1;
  }
  return counts;
}

// ---------------------------------------------------------------------------
// Row building
// ---------------------------------------------------------------------------

const str = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined);

/**
 * One row per device, plus one row for any unit that has no matching device
 * check-in (a unit installed but never heard from is exactly the thing you
 * need to see — dropping it would hide it).
 *
 * TWO JOIN KEYS, tried in that order:
 *
 *  1. `unit.deviceId` — the paired terminal's `term_…` id. Exact, and the one
 *     to prefer the moment a unit actually carries it.
 *  2. The unit's `trackingId` appearing in the device's `registerName`. Not a
 *     heuristic anyone invented here: `CreateRegisterPanel` REFUSES to create a
 *     register whose name omits the tracking id, saying "it is how this
 *     register is matched back to the device" — this is the other half of that
 *     contract. Names in use look like `Machan- Dual Screen · RTA-000015`.
 *
 * What is deliberately NOT a join key is a matching `appBuild`: two tills
 * flashed from the same build on the same bench share one, and collapsing them
 * would merge two real devices into one row.
 *
 * @param devices  device rows from `GET /devices/{deviceType}/status`
 * @param units    hardware units (may be `[]`; see {@link FleetUnitRecord.deviceId})
 * @param resolved deviceId → resolved target. Pass `{}` when no source is
 *                 available; the target/drift columns then read "unknown"
 *                 rather than guessing.
 */
export function buildFleetRows(
  devices: readonly FleetDeviceRecord[],
  units: readonly FleetUnitRecord[] = [],
  resolved: ResolvedTargetMap = {},
  options: FleetRowOptions = {},
  registerLinks: FleetRegisterLinkMap = {},
): FleetRow[] {
  const unitByDeviceId = new Map<string, FleetUnitRecord>();
  for (const u of units) {
    const id = str(u.deviceId);
    if (id && !unitByDeviceId.has(id)) unitByDeviceId.set(id, u);
  }
  const unitById = new Map(units.map((u) => [u.unitId, u]));

  const matchedUnitIds = new Set<string>();

  /** Fallback join: the register was named to carry the unit's tracking id. */
  const byRegisterName = (d: FleetDeviceRecord): FleetUnitRecord | undefined => {
    const name = str(d.registerName)?.toUpperCase();
    if (!name) return undefined;
    return units.find((u) => {
      const t = str(u.trackingId)?.toUpperCase();
      // A unit already claimed by an exact deviceId match is never re-matched
      // here — one device, one unit, and the exact key always wins.
      return !!t && !matchedUnitIds.has(u.unitId) && name.includes(t);
    });
  };

  // PASS 1 - exact deviceId on the unit. Unchanged, and still first.
  const exact = new Map<number, FleetUnitRecord>();
  devices.forEach((d, i) => {
    const u = unitByDeviceId.get(d.deviceId);
    if (u) { exact.set(i, u); matchedUnitIds.add(u.unitId); }
  });

  // PASS 2 - the registers table, which STATES the pairing. Runs before the
  // name heuristic so a stated pairing always beats a guessed one, and claims
  // its units before the guess gets to look.
  const viaRegister = new Map<number, FleetUnitRecord>();
  devices.forEach((d, i) => {
    if (exact.has(i)) return;
    const unitId = str(registerLinks[d.deviceId]?.deviceUnitId);
    if (!unitId || matchedUnitIds.has(unitId)) return;
    const u = unitById.get(unitId);
    if (u) { viaRegister.set(i, u); matchedUnitIds.add(u.unitId); }
  });

  const rows: FleetRow[] = devices.map((d, i) => {
    const stated = exact.get(i) ?? viaRegister.get(i);
    const unit = stated ?? byRegisterName(d);
    if (unit) matchedUnitIds.add(unit.unitId);
    const joinSource: FleetRow['unitJoinSource'] =
      !unit ? 'none' : stated ? 'register' : 'name';
    return makeRow(d, unit, lookupTarget(resolved, d, options), options, registerLinks[d.deviceId], joinSource);
  });

  for (const u of units) {
    if (matchedUnitIds.has(u.unitId)) continue;
    rows.push(makeRow(null, u, undefined, options, undefined, 'none'));
  }

  return rows;
}

/**
 * Target lookup for one device row.
 *
 * The compound {@link fleetTargetKey} is the ONLY key a map built by
 * {@link resolvedTargetMapFromEntries} ever contains, so cross-type collisions
 * cannot happen. The bare-`deviceId` fallback exists solely for a caller that
 * hand-builds a single-device-type map (tests, and callers predating the key
 * change); it is unreachable for mapper-built maps and therefore cannot
 * reintroduce the collision.
 */
function lookupTarget(
  resolved: ResolvedTargetMap,
  d: FleetDeviceRecord,
  options: FleetRowOptions,
): ResolvedTargetInfo | null | undefined {
  const type = str(d.deviceType) ?? str(options.deviceType);
  const keyed = resolved[fleetTargetKey(type, d.deviceId)];
  if (keyed !== undefined) return keyed;
  return Object.prototype.hasOwnProperty.call(resolved, d.deviceId) ? resolved[d.deviceId] : undefined;
}

function makeRow(
  d: FleetDeviceRecord | null,
  unit: FleetUnitRecord | undefined,
  target: ResolvedTargetInfo | null | undefined,
  options: FleetRowOptions,
  link: FleetRegisterLink | undefined,
  unitJoinSource: FleetRow['unitJoinSource'],
): FleetRow {
  const appVersion = str(d?.appVersion);
  const lastKnownInstallBuild = str(unit?.appBuild);
  const targetVersion = str(target?.version);
  const targetSource = target?.source === 'pin' || target?.source === 'ring' ? target.source : undefined;
  const pinnedVersion = str(d?.pinnedVersion);

  const partial = {
    appVersion,
    pinnedVersion,
    targetVersion,
    updatedAt: d?.updatedAt ?? null,
  };

  return {
    key: d ? d.deviceId : `unit:${unit?.unitId ?? 'unknown'}`,
    deviceId: d?.deviceId,
    deviceType: d?.deviceType,
    unitId: unit?.unitId,
    serialNumber: unit?.serialNumber,

    // Unit-side provenance. Absent on a pure check-in row that has no unit
    // joined to it — the table renders the photo and the order trace only when
    // it genuinely knows which physical device the row is.
    trackingId: str(unit?.trackingId),
    modelId: str(unit?.modelId),
    orderId: str(unit?.orderId),
    installedAt: unit?.installedAt ?? undefined,
    installSource: unit?.installSource ?? undefined,
    installTarget: str(unit?.installTarget),
    installVersionCode: unit?.installVersionCode ?? undefined,
    installedBy: str(unit?.installedBy),
    unitState: str(unit?.state),
    reinstallRequestedAt: unit?.reinstallRequestedAt ?? undefined,

    // Pass-through, nulls preserved — the tab's existing `?? '—'` renders them.
    registerName: d?.registerName,
    locationName: d?.locationName,
    // The register's locationId is the one the pairing recorded; the check-in's
    // is what the till last reported. Prefer the check-in (it is live), fall
    // back to the register (it is at least stated).
    locationId: d?.locationId ?? str(link?.locationId) ?? null,
    assignedChannel: d?.assignedChannel,
    bootVersion: d?.bootVersion,
    loadingVersion: d?.loadingVersion,
    bootInstalled: d?.bootInstalled,
    error: d?.error,
    updatedAt: d?.updatedAt,

    appVersion,
    appId: str(d?.appId),
    hardwareModel: str(d?.hardwareModel),
    hardwareBrand: str(d?.hardwareBrand),
    pendingUploads: d?.pendingUploads && Number.isFinite(d.pendingUploads.sales) ? d.pendingUploads : undefined,
    updateState: d?.updateState || undefined,
    updateVersion: str(d?.updateVersion),
    updateError: str(d?.updateError),
    lastKnownInstallBuild,
    displayVersion: appVersion ?? lastKnownInstallBuild,
    versionSource: appVersion ? 'checkin' : lastKnownInstallBuild ? 'install' : 'none',
    releaseRing: str(d?.releaseRing) ?? 'stable',
    pinnedVersion,
    // Task 10: absent, null and undefined ALL mean ON. Mirrors
    // deviceStatus.ts's own `prev.autoUpdate === false ? false : true`.
    autoUpdate: d?.autoUpdate === false ? false : true,
    targetVersion,
    targetSource,
    drift: !!appVersion && !!targetVersion && appVersion !== targetVersion,
    releaseStatus: classifyDeviceRelease(partial, options),

    hasDeviceRecord: !!d,
    hasUnitRecord: !!unit,
    device: d ?? undefined,

    tenantId: str(link?.tenantId),
    provisionCode: str(link?.provisionCode),
    unitJoinSource,
  };
}

// ---------------------------------------------------------------------------
// Ledger
// ---------------------------------------------------------------------------

/**
 * Collapses identical ADJACENT transitions in a device's version ledger.
 *
 * Phase C's accepted limit: a tight concurrent check-in window can record the
 * same transition twice (failures bias to over-recording, which is correct for
 * an audit log). Two rows are the same transition when their from/to versions,
 * app, method and source all match AND they sit next to each other — a genuine
 * v1→v2, v2→v1, v1→v2 history is not adjacent and is fully preserved.
 *
 * The surviving row of a collapsed run keeps the EARLIEST `at`: that is when
 * the transition actually happened; the duplicate is the later artefact.
 * Order of the input is otherwise preserved (the endpoint returns newest first).
 */
export function dedupeLedgerEntries<T extends FleetLedgerEntry>(entries: readonly T[]): T[] {
  const out: T[] = [];
  for (const e of entries) {
    const prev = out[out.length - 1];
    if (prev && sameTransition(prev, e)) {
      if (e.at < prev.at) out[out.length - 1] = e;
      continue;
    }
    out.push(e);
  }
  return out;
}

function sameTransition(a: FleetLedgerEntry, b: FleetLedgerEntry): boolean {
  return (
    (a.fromVersion ?? null) === (b.fromVersion ?? null) &&
    (a.toVersion ?? null) === (b.toVersion ?? null) &&
    (a.appId ?? null) === (b.appId ?? null) &&
    (a.via ?? null) === (b.via ?? null) &&
    (a.source ?? null) === (b.source ?? null)
  );
}
